import {
  VStack,
  HStack,
  Text,
  Button,
  Progress,
  Card,
  CardBody,
  Badge,
  Box,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { MdArrowBack, MdLock, MdRefresh } from 'react-icons/md';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/client/components/shared/LoadingSpinner';
import { EmptyState } from '@/client/components/shared/EmptyState';
import { TaskCardV2 } from '../components/TaskCardV2';
import { TaskInputModal } from '../components/TaskInputModal';
import { KanbanBoard } from '../components/KanbanBoard';
import { useWorkflowPage } from '../hooks';

export function WorkflowPage() {
  const p = useWorkflowPage();

  if (p.isStarting || p.isLoadingTasks) {
    return <LoadingSpinner message="Loading workflow..." />;
  }

  if (p.tasksError) {
    return (
      <PageContainer title="Workflow" description="Complete your workflow tasks" breadcrumbs={p.breadcrumbs}>
        <EmptyState
          title="Error loading workflow"
          description={p.tasksError.message || 'Failed to load workflow data'}
        />
      </PageContainer>
    );
  }

  if (!p.workflowData || p.tasks.length === 0) {
    return (
      <PageContainer title="Workflow Complete" description="All tasks completed" breadcrumbs={p.breadcrumbs}>
        <EmptyState title="No tasks available" description="This workflow has no tasks or all tasks are complete." />
        <Button mt={4} leftIcon={<MdArrowBack />} onClick={() => p.navigate(p.backPath)}>
          {p.backLabel}
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={p.workflowData.chatbot.title}
      description="Complete the workflow tasks"
      breadcrumbs={p.breadcrumbs}
    >
      <VStack spacing={6} align="stretch">
        <Card bgGradient={p.cardGradient}>
          <CardBody>
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="text-primary">Progress</Text>
                <HStack spacing={2}>
                  <Badge colorScheme="brand" fontSize="sm">{p.completedTasks} / {p.totalTasks} completed</Badge>
                  <Tooltip label="Refresh tasks">
                    <IconButton
                      aria-label="Refresh tasks" icon={<MdRefresh />} size="xs" variant="ghost"
                      isLoading={p.isRefetching} onClick={() => p.refetchTasks()}
                    />
                  </Tooltip>
                </HStack>
              </HStack>
              <Progress value={p.progress} colorScheme="brand" size="sm" borderRadius="full" />
              {p.workflowData.has_makerchecker_pending && (
                <HStack spacing={2}>
                  <Icon as={MdLock} color="orange.500" />
                  <Text fontSize="xs" color="orange.500">
                    Approval pending - some tasks are blocked until approved
                  </Text>
                </HStack>
              )}
            </VStack>
          </CardBody>
        </Card>

        <Box display={{ base: 'none', lg: 'block' }}>
          <KanbanBoard tasks={p.kanbanTasks} onTaskClick={p.handleOpenTask} />
        </Box>

        <VStack spacing={3} align="stretch" display={{ base: 'flex', lg: 'none' }}>
          {p.kanbanTasks.map(({ taskItem, status, index }) => (
            <TaskCardV2
              key={taskItem.task.id}
              title={taskItem.task.task_name}
              number={index + 1}
              status={status}
              displayValue={taskItem.display_value}
              softDueDate={taskItem.task.soft_due_date}
              userName={taskItem.task.user_name}
              updatedAt={taskItem.task.updated_at}
              notes={taskItem.task.notes}
              description={taskItem.task.description}
              documentLinks={taskItem.task.document_links ?? []}
              onClick={() => p.handleOpenTask(taskItem)}
            />
          ))}
        </VStack>

        <Button leftIcon={<MdArrowBack />} variant="outline" onClick={() => p.navigate(p.backPath)}>
          {p.backLabel}
        </Button>
      </VStack>

      <TaskInputModal
        isOpen={p.isOpen}
        onClose={p.handleCloseModal}
        task={p.selectedTask}
        onSubmitText={p.handleSubmitText}
        onSubmitMedia={p.handleSubmitMedia}
        onSubmitChoice={p.handleSubmitChoice}
        isLoading={p.isSubmitting}
        botType={p.workflowData?.chatbot?.bot_type}
        onRefresh={async () => { await p.refetchTasks(); }}
        onConfirmWip={p.handleConfirmWip}
        isConfirmingWip={p.isConfirmingWip}
        isWipConfigured={p.isSelectedTaskWipConfigured}
        taskDescription={p.selectedTask?.task.description}
      />
    </PageContainer>
  );
}
